import decode from 'jwt-decode';

import { captureException } from '@playbookux/playbook-web-core';

// eslint-disable-next-line import/no-unresolved
import useStore from 'playbook-web/store';

import {
  AUTHENTICATION_CHANGE_COMPANY,
  AUTHENTICATION_COMPLETED,
  AUTHENTICATION_CREATE_COMPANY,
  AUTHENTICATION_FAILURE,
  AUTHENTICATION_GET_COMPANIES_COMPLETED,
  AUTHENTICATION_GET_PROFILE_COMPLETED,
  AUTHENTICATION_GET_PROFILE_FAILURE,
  AUTHENTICATION_GET_PROFILE,
  AUTHENTICATION_INITIALIZED,
  AUTHENTICATION_LINKED_IN_STATE,
  AUTHENTICATION_REFERRAL,
  AUTHENTICATION_SIGN_IN_WITH_LINKED_IN_COMPLETED,
  AUTHENTICATION_SIGN_IN_SSO_COMPLETED,
  AUTHENTICATION_SIGN_IN_WORKOS_COMPLETED,
  AUTHENTICATION_SIGN_IN_WITH_PASSWORD_COMPLETED,
  AUTHENTICATION_SIGN_OUT,
  AUTHENTICATION_SIGN_IN_WITH_SHARED_CODE,
  AUTHENTICATION_SIGN_IN_WITH_LIVESTREAM,
  AUTHENTICATION_SCHEDULE,

  // FIXME: Refactor action types bellow

  AUTHENTICATION_SEND_PROFILE_INFORMATION_COMPLETED,
  AUTHENTICATION_SEND_PROFILE_INFORMATION_FAILURE,
  AUTHENTICATION_SEND_PROFILE_INFORMATION,
  AUTHENTICATION_SIGN_IN_FAILED,
  AUTHENTICATION_SIGN_IN,
  AUTHENTICATION_SIGN_IN_TESTER,
  AUTHENTICATION_USER_STATUS,
  SET_USER,
  AUTHENTICATION_MODERATED_MEETING,
} from './action-types';

import { getProfile, sendProfileInformation } from '../services/profile';
import { getCompanyMember } from '../services/graphql-requests';

import { setCoupon } from './parameters';

import {
  authenticate, // FIXME: Rename this method to `authenticateWithScreenerSession`
  authenticateWithLinkedIn,
  authenticateWithPathfix,
  authenticateWithPassword,
  authenticateWithShareCode,
  authenticateWithLivestream,
  authenticateSchedule,
  authenticateWithWorkOS,
  workOSCallback,
  authenticateWithTester,
  authenticateModeratedMeeting,
} from '../services/authentication';

export const setLinkedInState = (state) => async (dispatch) => {
  const payload = {
    state,
  };

  dispatch({
    type: AUTHENTICATION_LINKED_IN_STATE,
    payload,
  });

  return payload;
};

export const createCompany = (company) => async (dispatch) => {
  const payload = {
    company,
  };

  dispatch({
    type: AUTHENTICATION_CREATE_COMPANY,
    payload,
  });
};

export const setCurrentCompany = (company) => async (dispatch) => {
  const payload = {
    company,
  };

  dispatch({
    type: AUTHENTICATION_CHANGE_COMPANY,
    payload,
  });

  return payload;
};

export const setReferral = (referral) => async (dispatch) => {
  const payload = {
    referral,
  };

  dispatch({
    type: AUTHENTICATION_REFERRAL,
    payload,
  });

  return payload;
};

export const setUser = (user) => async (dispatch) => {
  const payload = {
    user,
  };

  dispatch({
    type: SET_USER,
    payload,
  });

  return payload;
};

export const fetchCompany = (companyId) => async (dispatch, getState) => {
  const { authentication } = getState();
  if (!authentication?.token) {
    return null;
  }

  const company = await getCompanyMember(companyId);

  dispatch({
    type: AUTHENTICATION_GET_COMPANIES_COMPLETED,
    payload: {
      company,
    },
  });
  return company;
};

export const signInWithPathfix = (oauthId, provider) => async (dispatch) => {
  dispatch({
    type: AUTHENTICATION_INITIALIZED,
  });

  try {
    const {
      access_token: token,
      intercom_user_hash: intercomUserHash,
    } = await authenticateWithPathfix(oauthId, provider);

    dispatch({
      type: AUTHENTICATION_SIGN_IN_SSO_COMPLETED,
      payload: {
        intercomUserHash,
        token,
      },
    });

    const profile = await getProfile();

    dispatch({
      type: AUTHENTICATION_GET_PROFILE_COMPLETED,
      payload: {
        profile,
      },
    });

    await dispatch(fetchCompany(null));

    dispatch({
      type: AUTHENTICATION_COMPLETED,
    });

    return decode(token);
  } catch (err) {
    dispatch({
      type: AUTHENTICATION_FAILURE,
    });

    throw err;
  }
};

export const signInWithLinkedIn = (code, referral) => async (dispatch) => {
  dispatch({
    type: AUTHENTICATION_INITIALIZED,
  });

  try {
    const {
      access_token: token,
      intercom_user_hash: intercomUserHash,
    } = await authenticateWithLinkedIn(code, referral);

    dispatch({
      type: AUTHENTICATION_SIGN_IN_WITH_LINKED_IN_COMPLETED,
      payload: {
        intercomUserHash,
        token,
      },
    });

    const profile = await getProfile();

    dispatch({
      type: AUTHENTICATION_GET_PROFILE_COMPLETED,
      payload: {
        profile,
      },
    });

    await dispatch(fetchCompany(null));

    dispatch({
      type: AUTHENTICATION_COMPLETED,
    });

    return decode(token);
  } catch (err) {
    captureException('SIGN_IN_WITH_LINKEDIN_FAILED', err);

    dispatch({
      type: AUTHENTICATION_FAILURE,
    });

    throw err;
  }
};

export const signInWithTester = (sessionId, token, isRecruitUser = false) => async (dispatch) => {
  dispatch({
    type: AUTHENTICATION_SIGN_IN,
  });

  try {
    const authentication = await authenticateWithTester(sessionId, token);

    const payload = {
      sessionId,
      accessToken: authentication.access_token,
      isRecruitUser,
    };

    dispatch({
      type: AUTHENTICATION_SIGN_IN_TESTER,
      payload,
    });

    return payload;
  } catch (err) {
    captureException('SIGN_IN_WITH_TESTER_FAILED', err);

    dispatch({
      type: AUTHENTICATION_SIGN_IN_FAILED,
    });

    throw err;
  }
};

export const signInSchedule = (segmentId) => async (dispatch) => {
  try {
    const {
      access_token: token,
    } = await authenticateSchedule(segmentId);

    dispatch({
      type: AUTHENTICATION_SCHEDULE,
      payload: {
        token,
      },
    });

    return decode(token);
  } catch (err) {
    dispatch({
      type: AUTHENTICATION_FAILURE,
    });

    throw err;
  }
};

export const signInModeratedMeeting = (testSessionId) => async (dispatch) => {
  try {
    const {
      access_token: token,
    } = await authenticateModeratedMeeting(testSessionId);

    dispatch({
      type: AUTHENTICATION_MODERATED_MEETING,
      payload: {
        token,
      },
    });

    return decode(token);
  } catch (err) {
    dispatch({
      type: AUTHENTICATION_FAILURE,
    });

    throw err;
  }
};

export const signInWithLivestream = (testSessionId) => async (dispatch) => {
  try {
    const {
      access_token: token,
    } = await authenticateWithLivestream(testSessionId);

    dispatch({
      type: AUTHENTICATION_SIGN_IN_WITH_LIVESTREAM,
      payload: {
        token,
        scope: 'livestream',
      },
    });

    dispatch({
      type: AUTHENTICATION_COMPLETED,
    });

    return token;
  } catch (err) {
    captureException('SIGN_IN_WITH_LIVESTREAM_FAILED', err);

    dispatch({
      type: AUTHENTICATION_FAILURE,
    });

    throw err;
  }
};

export const signInWithShareCode = (shareType, shareCode, password) => async (dispatch) => {
  try {
    const {
      access_token: token,
    } = await authenticateWithShareCode(shareType, shareCode, password);

    dispatch({
      type: AUTHENTICATION_SIGN_IN_WITH_SHARED_CODE,
      payload: {
        token,
        scope: shareType,
      },
    });

    dispatch({
      type: AUTHENTICATION_COMPLETED,
    });

    return decode(token);
  } catch (err) {
    captureException('SIGN_IN_WITH_SHARE_CODE_FAILED', err);

    dispatch({
      type: AUTHENTICATION_FAILURE,
    });

    throw err;
  }
};

export const getCurrentProfile = () => async (dispatch) => {
  dispatch({
    type: AUTHENTICATION_GET_PROFILE,
  });

  try {
    const profile = await getProfile();

    const payload = {
      profile,
    };

    dispatch({
      type: AUTHENTICATION_GET_PROFILE_COMPLETED,
      payload,
    });

    return payload;
  } catch (err) {
    captureException('GET_CURRENT_PROFILE_FAILED', err);
    dispatch({
      type: AUTHENTICATION_GET_PROFILE_FAILURE,
    });

    throw err;
  }
};

export const patchProfile = (profileId, { newsletter, ...form }) => async (dispatch) => {
  dispatch({
    type: AUTHENTICATION_SEND_PROFILE_INFORMATION,
  });

  try {
    await sendProfileInformation(profileId, form, newsletter);
    dispatch({
      type: AUTHENTICATION_SEND_PROFILE_INFORMATION_COMPLETED,
    });
  } catch (err) {
    captureException('PATCH_PROFILE_FAILED', err);

    dispatch({
      type: AUTHENTICATION_SEND_PROFILE_INFORMATION_FAILURE,
    });

    throw err;
  }
};

export const authenticateWithScreener = (sessionId, token, isRecruitUser = false) => async (dispatch) => {
  dispatch({
    type: AUTHENTICATION_SIGN_IN,
  });

  try {
    const authentication = await authenticate(sessionId, token);
    const payload = {
      sessionId,
      accessToken: authentication.access_token,
      isRecruitUser,
    };

    dispatch({
      type: AUTHENTICATION_SIGN_IN_TESTER,
      payload,
    });

    return payload;
  } catch (err) {
    captureException('AUTHENTICATE_WITH_SCREENER_FAILED', err);

    dispatch({
      type: AUTHENTICATION_SIGN_IN_FAILED,
    });

    throw err;
  }
};

export const signOut = () => async (dispatch) => {
  dispatch(setCoupon(null));
  useStore.setState({ coupon: null });
  // SessionStack
  // clear user identity
  if (window.SessionStack) {
    window.SessionStack.identify(false);
  }
  dispatch({
    type: AUTHENTICATION_SIGN_OUT,
  });
};

export const signInWithPassword = (email, password, recaptcha = { recaptchaToken: '', recaptchaVersion: '' }) => async (dispatch) => {
  dispatch({
    type: AUTHENTICATION_INITIALIZED,
  });

  try {
    const {
      access_token: token,
      intercom_user_hash: intercomUserHash,
    } = await authenticateWithPassword(email, password, recaptcha);

    dispatch({
      type: AUTHENTICATION_SIGN_IN_WITH_PASSWORD_COMPLETED,
      payload: {
        intercomUserHash,
        token,
      },
    });

    const profile = await getProfile();

    dispatch({
      type: AUTHENTICATION_GET_PROFILE_COMPLETED,
      payload: {
        profile,
      },
    });

    await dispatch(fetchCompany(null));

    dispatch({
      type: AUTHENTICATION_COMPLETED,
    });

    return decode(token);
  } catch (err) {
    captureException('SIGN_IN_WITH_PASSWORD_FAILED', err);

    dispatch({
      type: AUTHENTICATION_FAILURE,
    });

    throw err;
  }
};

export const signInWithWorkOS = (code) => async (dispatch) => {
  dispatch({
    type: AUTHENTICATION_INITIALIZED,
  });

  try {
    const {
      access_token: token,
      intercom_user_hash: intercomUserHash,
    } = await workOSCallback(code);

    dispatch({
      type: AUTHENTICATION_SIGN_IN_WORKOS_COMPLETED,
      payload: {
        intercomUserHash,
        token,
      },
    });

    const profile = await getProfile();

    dispatch({
      type: AUTHENTICATION_GET_PROFILE_COMPLETED,
      payload: {
        profile,
      },
    });

    await dispatch(fetchCompany(null));

    dispatch({
      type: AUTHENTICATION_COMPLETED,
    });
    return decode(token);
  } catch (err) {
    captureException('SIGN_IN_WITH_WORKOS_FAILED', err);

    dispatch({
      type: AUTHENTICATION_FAILURE,
    });

    throw err;
  }
};

export const startWorkOSAuth = (email) => async (dispatch) => {
  // dispatch({
  //   type: AUTHENTICATION_INITIALIZED,
  // });

  try {
    const { authorization_url: url } = await authenticateWithWorkOS(email);

    // dispatch({
    //   type: AUTHENTICATION_SIGN_IN_SSO_COMPLETED,
    //   payload: {
    //     intercomUserHash,
    //     token,
    //   },
    // });

    return url;
  } catch (err) {
    captureException('START_WORK_OS_AUTH_FAILED', err);

    dispatch({
      type: AUTHENTICATION_FAILURE,
    });

    throw err;
  }
};

// this is only used for header menu for not doing extra refresh on page.
export const updateUserStatus = (statusParams) => async (dispatch, getState) => {
  const { status } = getState().authentication;
  if (statusParams !== undefined && statusParams !== status) {
    dispatch({
      type: AUTHENTICATION_USER_STATUS,
      payload: { status: statusParams },
    });
  }
};
